import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Loader2, ShieldCheck, MessageCircle } from 'lucide-react';
import { publicApi } from '../lib/api';
import { pricingCategories, contactInfo } from '../data/mock';
import { useToast } from '../hooks/use-toast';

const parsePrice = (s) => {
  if (!s) return 0;
  const n = parseFloat(String(s).replace(/[^0-9.]/g, ''));
  return isNaN(n) ? 0 : n;
};

// Flatten all treatments with prices
const allTreatments = pricingCategories
  .filter((c) => !['upsells', 'oils'].includes(c.id))
  .flatMap((c) =>
    c.services.map((s) => ({
      key: `${c.title} \u2014 ${s.name}`,
      category: c.title,
      name: s.name,
      price: parsePrice(s.price),
      duration: c.duration,
    }))
  );

const DepositPay = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    treatmentKey: '',
    preferred_date: '',
    preferred_time: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const selected = useMemo(() => allTreatments.find((t) => t.key === form.treatmentKey), [form.treatmentKey]);
  const deposit = selected ? +(selected.price * 0.5).toFixed(2) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.treatmentKey) {
      toast({ title: 'Please fill in your name, email and pick a treatment' });
      return;
    }
    setSubmitting(true);
    try {
      const r = await publicApi.post('/deposits/create', {
        name: form.name,
        email: form.email,
        phone: form.phone,
        treatment: selected.key,
        treatment_price: selected.price,
        preferred_date: form.preferred_date,
        preferred_time: form.preferred_time,
      });
      // Auto-submit form to PayFast
      const f = document.createElement('form');
      f.method = 'POST';
      f.action = r.data.action_url;
      Object.entries(r.data.fields).forEach(([k, v]) => {
        const i = document.createElement('input');
        i.type = 'hidden'; i.name = k; i.value = v == null ? '' : String(v);
        f.appendChild(i);
      });
      document.body.appendChild(f);
      f.submit();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not start payment. Please WhatsApp Dawn to arrange.';
      toast({ title: 'Payment unavailable', description: detail });
      setSubmitting(false);
    }
  };

  return (
    <section id="deposit" className="relative py-16 md:py-20">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.8 }}
          className="bg-white/20 backdrop-blur-md rounded-3xl border border-white/30 p-8 md:p-10 shadow-2xl"
        >
          <div className="flex items-center gap-3 mb-3 text-white">
            <ShieldCheck size={22} strokeWidth={1.6} />
            <p className="tracking-[0.3em] text-xs" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
              STEP 2 &middot; SECURE YOUR SLOT
            </p>
          </div>
          <h3 className="text-white font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.7rem, 3vw, 2.4rem)' }}>
            Pay a 50% deposit
          </h3>
          <p className="text-white/85 mb-6 italic" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.05rem' }}>
            After picking your Calendly slot above, complete your booking with a 50% deposit here. The balance is paid to Dawn on the day.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="NAME" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Your name" />
              <Field label="PHONE" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="Contact number" />
            </div>
            <Field label="EMAIL" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="you@example.com" />

            <div>
              <label className="block text-white/80 text-xs tracking-[0.25em] mb-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>TREATMENT</label>
              <select value={form.treatmentKey} onChange={(e) => setForm({ ...form, treatmentKey: e.target.value })}
                className="w-full bg-transparent border-b border-white/40 focus:border-white text-white py-2 outline-none"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.05rem' }}>
                <option value="" className="text-neutral-800">Choose your treatment</option>
                {allTreatments.map((t) => (
                  <option key={t.key} value={t.key} className="text-neutral-800">
                    {t.duration} \u2014 {t.category} \u2014 {t.name} \u2014 R{t.price}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="BOOKED DATE (FROM CALENDLY)" type="date" value={form.preferred_date} onChange={(v) => setForm({ ...form, preferred_date: v })} />
              <Field label="BOOKED TIME" type="time" value={form.preferred_time} onChange={(v) => setForm({ ...form, preferred_time: v })} />
            </div>

            {selected && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-between bg-white/25 border border-white/40 rounded-2xl px-5 py-4 text-white"
              >
                <div>
                  <p className="text-xs tracking-[0.25em] opacity-90" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                    50% DEPOSIT
                  </p>
                  <p className="text-sm mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Treatment total: <b>R{selected.price.toLocaleString()}</b> &middot; Balance on the day: R{(selected.price - deposit).toLocaleString()}
                  </p>
                </div>
                <p className="text-3xl md:text-4xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  R{deposit.toLocaleString()}
                </p>
              </motion.div>
            )}

            <div className="flex flex-wrap gap-3 pt-2">
              <button type="submit" disabled={submitting || !selected}
                className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] font-light bg-white/5 hover:bg-white hover:text-rose-500 transition-all duration-500 disabled:opacity-60"
                style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                {submitting ? (<><Loader2 size={14} className="animate-spin" /> REDIRECTING TO PAYFAST</>) : (<><CreditCard size={14} /> PAY 50% DEPOSIT</>)}
              </button>
              <a href={`https://wa.me/${contactInfo.whatsappIntl}?text=${encodeURIComponent("Hi Dawn! I'd like to arrange payment differently. Could you help?")}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] bg-white/5 hover:bg-white hover:text-rose-500 transition-all"
                style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                <MessageCircle size={14} /> ARRANGE ANOTHER WAY
              </a>
            </div>

            <p className="text-xs text-white/70 mt-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Secured by PayFast &middot; Cards, Instant EFT, SnapScan, Zapper &middot; Your deposit is non-refundable for last-minute cancellations. Cancel at least 1 day before for a refund.
            </p>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

const Field = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="block text-white/80 text-xs tracking-[0.25em] mb-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border-b border-white/40 focus:border-white text-white placeholder-white/50 py-2 outline-none"
      placeholder={placeholder} style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.05rem', colorScheme: 'dark' }} />
  </div>
);

export default DepositPay;
